import { useEffect, useState } from 'react'
import { Check, LockKeyhole, MessageSquare, Phone, Save } from 'lucide-react'

import { supabase } from '../lib/supabase'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Switch } from './ui/switch'
import { TeamRole } from '../types/supabase'

type Channel = 'email' | 'sms'

interface NotificationContactSettingsProps {
  teamId: string
  userRole: TeamRole
}

export function NotificationContactSettings({ teamId, userRole }: NotificationContactSettingsProps) {
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [code, setCode] = useState('')
  const [pods, setPods] = useState<{ id: string; name: string }[]>([])
  const [podSettings, setPodSettings] = useState<Record<string, { enabled: boolean; channels: Channel[] }>>({})
  const [channels, setChannels] = useState<Channel[]>(['email'])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [verificationPending, setVerificationPending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const isManager = userRole === 'manager'

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const { data: auth } = await supabase.auth.getUser()
      if (!auth.user || cancelled) return
      setLoading(true)
      setError('')
      try {
        const [{ data: contact, error: contactError }, { data: preference, error: preferenceError }] = await Promise.all([
          (supabase as any).from('notification_contacts').select('email_address, phone_number, phone_verified_at').eq('auth_user_id', auth.user.id).maybeSingle(),
          (supabase as any).from('notification_preferences').select('channels').eq('recipient_user_id', auth.user.id).eq('source_type', 'budget_threshold').maybeSingle(),
        ])
        if (contactError) throw contactError
        if (preferenceError) throw preferenceError
        if (cancelled) return
        setEmail(contact?.email_address ?? auth.user.email ?? '')
        setPhone(contact?.phone_number ?? '')
        setPhoneVerified(Boolean(contact?.phone_verified_at))
        setChannels((preference?.channels ?? ['email']).filter((channel: string): channel is Channel => channel === 'email' || channel === 'sms'))

        if (isManager) {
          const { data: assignments, error: assignmentsError } = await (supabase as any)
            .from('pod_managers').select('pod_id').eq('auth_user_id', auth.user.id)
          if (assignmentsError) throw assignmentsError
          const podIds = (assignments ?? []).map((row: any) => row.pod_id)
          if (podIds.length) {
            const [{ data: podRows, error: podsError }, { data: settings, error: settingsError }] = await Promise.all([
              (supabase as any).from('pods').select('id, name').in('id', podIds).eq('team_id', teamId).order('name'),
              (supabase as any).from('notification_pod_preferences').select('pod_id, enabled, channels').in('pod_id', podIds),
            ])
            if (podsError) throw podsError
            if (settingsError) throw settingsError
            setPods(podRows ?? [])
            setPodSettings(Object.fromEntries((podRows ?? []).map((pod: any) => {
              const setting = (settings ?? []).find((row: any) => row.pod_id === pod.id)
              return [pod.id, { enabled: setting?.enabled ?? true, channels: (setting?.channels ?? ['email']).filter((channel: string): channel is Channel => channel === 'email' || channel === 'sms') }]
            })))
          }
        }
      } catch (loadError: any) {
        if (!cancelled) setError(loadError.message || 'Failed to load notification settings')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [isManager, teamId])

  const invoke = async (body: Record<string, unknown>) => {
    const { data, error: invokeError } = await supabase.functions.invoke('notification-contacts', { body })
    if (invokeError) throw invokeError
    if (data?.error) throw new Error(data.error)
    return data
  }

  const save = async () => {
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      await invoke({ action: 'save_contact', email, phone: phone || null })
      const auth = await supabase.auth.getUser()
      if (!auth.data.user) throw new Error('Your session has expired')
      const authUser = auth.data.user
      const sourceTypes = ['budget_threshold', 'model_usage', 'anomaly', 'standup_escalation', 'manager_digest']
      const { error: preferenceError } = await (supabase as any).from('notification_preferences').upsert(
        sourceTypes.map((source_type) => ({ recipient_user_id: authUser.id, source_type, channels })),
        { onConflict: 'recipient_user_id,source_type' },
      )
      if (preferenceError) throw preferenceError
      if (isManager) {
        for (const pod of pods) {
          const setting = podSettings[pod.id]
          const { error: podError } = await (supabase as any).from('notification_pod_preferences').upsert({
            manager_auth_user_id: authUser.id,
            pod_id: pod.id,
            enabled: setting.enabled,
            channels: setting.channels,
          }, { onConflict: 'manager_auth_user_id,pod_id' })
          if (podError) throw podError
        }
      }
      setSuccess('Notification settings saved.')
    } catch (saveError: any) {
      setError(saveError.message || 'Failed to save notification settings')
    } finally {
      setSaving(false)
    }
  }

  const startVerification = async () => {
    setError('')
    setSuccess('')
    try {
      await invoke({ action: 'start_phone_verification', phone })
      setVerificationPending(true)
      setSuccess('Verification code sent.')
    } catch (verificationError: any) {
      setError(verificationError.message || 'Could not send verification code')
    }
  }

  const verifyPhone = async () => {
    setError('')
    try {
      await invoke({ action: 'verify_phone', phone, code })
      setPhoneVerified(true)
      setVerificationPending(false)
      setCode('')
      setSuccess('Phone number verified.')
    } catch (verificationError: any) {
      setError(verificationError.message || 'Invalid verification code')
    }
  }

  const toggleChannel = (channel: Channel) => setChannels((current) => current.includes(channel) ? current.filter((value) => value !== channel) : [...current, channel])
  const updatePod = (podId: string, patch: Partial<{ enabled: boolean; channels: Channel[] }>) => setPodSettings((current) => ({ ...current, [podId]: { ...current[podId], ...patch } }))

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading notification settings…</div>

  return (
    <section className="space-y-5 border-t border-border pt-6">
      <div className="flex items-start gap-3"><MessageSquare className="mt-0.5 h-5 w-5 text-primary" /><div><h3 className="text-sm font-semibold">Email and SMS notifications</h3><p className="mt-1 text-xs text-muted-foreground">Choose how you receive eligible budget and model-usage alerts.</p></div></div>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {success && <p role="status" className="flex items-center gap-2 text-sm text-emerald-600"><Check className="h-4 w-4" />{success}</p>}
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="space-y-2 text-sm"><span className="font-medium">Email address</span><Input value={email} onChange={(event) => setEmail(event.target.value)} type="email" /></label>
        <label className="space-y-2 text-sm"><span className="font-medium">Phone number</span><Input value={phone} onChange={(event) => { setPhone(event.target.value); setPhoneVerified(false) }} placeholder="+14155552671" inputMode="tel" /></label>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <Button type="button" variant="outline" size="sm" onClick={startVerification} disabled={!phone || phoneVerified}><Phone className="mr-2 h-4 w-4" />{phoneVerified ? 'Phone verified' : 'Send verification code'}</Button>
        {phoneVerified && <span className="text-emerald-600">Verified</span>}
      </div>
      {verificationPending && <div className="flex gap-2"><Input className="max-w-40" value={code} onChange={(event) => setCode(event.target.value)} placeholder="6-digit code" inputMode="numeric" /><Button type="button" onClick={verifyPhone} disabled={code.length < 4}>Verify</Button></div>}
      <div className="flex flex-wrap gap-5 text-sm"><label className="flex items-center gap-2"><Switch checked={channels.includes('email')} onCheckedChange={() => toggleChannel('email')} />Email</label><label className="flex items-center gap-2"><Switch checked={channels.includes('sms')} onCheckedChange={() => toggleChannel('sms')} disabled={!phoneVerified} />SMS {!phoneVerified && <LockKeyhole className="h-3.5 w-3.5 text-muted-foreground" />}</label></div>
      {isManager && pods.length > 0 && <div className="space-y-3"><div><h4 className="text-sm font-semibold">Pod preferences</h4><p className="mt-1 text-xs text-muted-foreground">You only receive alerts for pods you manage and enable here.</p></div>{pods.map((pod) => { const setting = podSettings[pod.id]; return <div key={pod.id} className="flex flex-wrap items-center justify-between gap-3 border border-border p-3"><label className="flex items-center gap-2 text-sm"><Switch checked={setting?.enabled ?? true} onCheckedChange={(enabled) => updatePod(pod.id, { enabled })} />{pod.name}</label><div className="flex gap-3 text-xs text-muted-foreground"><label className="flex items-center gap-2"><Switch className="scale-75" checked={setting?.channels.includes('email') ?? true} onCheckedChange={() => updatePod(pod.id, { channels: setting.channels.includes('email') ? setting.channels.filter((channel) => channel !== 'email') : [...setting.channels, 'email'] })} />Email</label><label className="flex items-center gap-2"><Switch className="scale-75" checked={setting?.channels.includes('sms') ?? false} onCheckedChange={() => updatePod(pod.id, { channels: setting.channels.includes('sms') ? setting.channels.filter((channel) => channel !== 'sms') : [...setting.channels, 'sms'] })} disabled={!phoneVerified} />SMS</label></div></div> })}</div>}
      <div className="flex justify-end"><Button onClick={() => void save()} disabled={saving || !channels.length}><Save className="mr-2 h-4 w-4" />{saving ? 'Saving…' : 'Save notification settings'}</Button></div>
    </section>
  )
}
